import { t } from 'i18next'

interface TableHeaderProps {
  isMobile?: boolean
}

export const TableHeader: FC<TableHeaderProps> = ({ isMobile=false }) => {
  return (
    <TableRow style={{
      backgroundColor: '#F5F5F5',
      borderBottomWidth: 2,
      borderBottomColor: '#E0E0E0',
      paddingVertical: 12,
      borderRadius: 8,
      marginBottom: 8,
    }}>

      {/* Status Column Header */}
      {!isMobile && (
        <StatusColumn>
          <Text type="headline_b" textAlign='center' color="foreground0">
            {t('common.status')}
          </Text>
        </StatusColumn>
      )}

      {/* Vaccine Column Header */}
      <VaccineColumn isMobile={isMobile}>
        <Text type="headline_b" color="foreground0">
          {t('common.vaccines')}
        </Text>
      </VaccineColumn>

      {/* Mandatory Column Header */}
      {!isMobile && <MandatoryColumn>
        <Text type="headline_b" textAlign={isMobile ? 'right' : 'center'} color="foreground0">
          {t('common.mandatory')}
        </Text>
      </MandatoryColumn>}

      {/* History Column Header */}
      <HistoryColumn isMobile={isMobile}>
        <Text type="headline_b" textAlign={isMobile ? 'right' : 'center'} color="foreground0">
          {t('common.detail')}
        </Text>
      </HistoryColumn>
    </TableRow>
  )
}

// Versión actualizada del ItemWithIcon que incluye la cabecera
import React, { FC, useState, useEffect } from 'react'
import {
  TableRow,
  StatusColumn,
  TypeColumn,
  TypeCircle,
  VaccineColumn,
  PeriodicityColumn,
  RenewalColumn,
  HistoryColumn,
  StatusCircle,
  MobileCard,
  MobileCardHeader,
  MobileCardContent,
  MobileCardRow,
  MandatoryColumn
} from './itemwithicon.styles'
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/arrow_chevron_right.svg'
import { Text } from '@ui/components'
import { Vaccine } from '@front/apis'
import { Dimensions, TouchableOpacity, View } from 'react-native'

export interface ItemWithIconProps {
  title: string
  description: string
  onPress?: () => void
  isMandatory?: boolean
  isUpToDate?: boolean
  periodicity?: string
  ageToVaccinate?: string
  showHistoryButton?: boolean
  onHistoryPress?: () => void
  vaccine?: Vaccine
  showStatus?: boolean
}

// Componente contenedor para la tabla con cabecera
interface VaccineTableProps {
  children: React.ReactNode
  showStatus?: boolean
}

export const VaccineTable: FC<VaccineTableProps> = ({ children, showStatus }) => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      const { width } = Dimensions.get('window')
      setIsMobile(width < 768)
    }

    checkScreenSize()
    const subscription = Dimensions.addEventListener('change', checkScreenSize)

    return () => subscription?.remove()
  }, [])

  return (
    <View>
      <TableHeader isMobile={isMobile} />
      {children}
    </View>
  )
}

// Function to calculate renewal date based on last vaccination and periodicity
const calculateRenewalDate = (vaccine: Vaccine): string | null => {
  if (!vaccine.history || vaccine.history.length === 0) {
    return null
  }

  // Get the most recent vaccination date - create a copy to avoid modifying the original array
  const sortedHistory = [...vaccine.history].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  const lastVaccination = sortedHistory[0]

  if (!lastVaccination) {
    return null
  }

  const lastDate = new Date(lastVaccination.date)
  const renewalDate = new Date(lastDate)

  // Add months based on periodicity
  if (vaccine.periodicity === 'ANNUAL') {
    renewalDate.setFullYear(renewalDate.getFullYear() + 1)
  } else if (vaccine.periodicity === 'ONCE') {
    return null // No renewal for one-time vaccines
  } else {
    // For other periodicities, use the afterHowManyMonths
    renewalDate.setMonth(renewalDate.getMonth() + vaccine.afterHowManyMonths)
  }

  return renewalDate.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export const ItemWithIcon: FC<ItemWithIconProps> = ({
  title,
  description,
  onPress,
  isMandatory,
  isUpToDate,
  periodicity,
  ageToVaccinate,
  showHistoryButton,
  onHistoryPress,
  vaccine,
  showStatus,
}) => {
  const renewalDate = vaccine ? calculateRenewalDate(vaccine) : null
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      const { width } = Dimensions.get('window')
      setIsMobile(width < 768) // Switch to mobile layout below 768px
    }

    checkScreenSize()
    const subscription = Dimensions.addEventListener('change', checkScreenSize)

    return () => subscription?.remove()
  }, [])

  if (isMobile) {
     return (
       <MobileCard onPress={onPress}>         
         <MobileCardContent>
           <MobileCardRow>
             <Text type="caption" color={isUpToDate ? 'success' : 'error'} style={{ fontSize: 16, fontWeight: 'bold' }}>
               {isUpToDate ? '✓  ' : '✗  '}
             </Text>
             <Text type="H5" fontWeight="bold" fontFamily="bold" style={{ flex: 1 }}>
                {title}{isMandatory && <Text type="caption">{` (${t('common.mandatory')})`}</Text> }<br/><Text type="caption">{description}</Text>
            </Text>

            <Text type="caption" color="primary" onPress={onHistoryPress}>{t('common.view')}</Text>
           </MobileCardRow>
           
           {renewalDate && false && (
             <MobileCardRow>
               <Text type="caption" color="primary">
                 Próx {renewalDate}
               </Text>
             </MobileCardRow>
           )}
         </MobileCardContent>
       </MobileCard>
     )
   }

  return (
    <TableRow>
      <StatusColumn>
        {showStatus && isUpToDate !== undefined && (
          <Text type="caption" color={isUpToDate ? 'success' : 'error'} style={{ fontSize: 16, fontWeight: 'bold' }}>
            {isUpToDate ? '✓' : '✗'}
          </Text>
        )}
      </StatusColumn>

      {/* Vaccine Name and Description */}
      <VaccineColumn>
        <Text type="H5" fontWeight="bold" fontFamily="bold" numberOfLines={1}>
          {title}{isMobile && <><br/><Text type="caption">{description}</Text></>}
        </Text>
        {!isMobile && <Text type="caption" numberOfLines={1}>
          {description}
        </Text>}
      </VaccineColumn>

      {/* Mandatory */}
      <MandatoryColumn>
        <Text type="caption">{isMandatory ? t('common.yes') : t('common.no')}</Text>
      </MandatoryColumn>

      {/* History Link */}
      <HistoryColumn>
        <TouchableOpacity style={{ flex: 1 }} onPress={() => onHistoryPress?.()}>
          <Text type="caption" color="primary">{t('common.view')}</Text>
          </TouchableOpacity>
      </HistoryColumn>
    </TableRow>
  )
}